import React, { useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Animated, {
    useSharedValue,
    useAnimatedStyle,
    withRepeat,
    withTiming,
    Easing,
    interpolate,
    withDelay,
} from 'react-native-reanimated';
import { theme } from '../constants/theme';

interface DetailScreenLoadingProps {
    message?: string;
    showMessage?: boolean;
}

// Animated dot component for the loading dots
const AnimatedDot: React.FC<{ delay: number }> = ({ delay }) => {
    const opacity = useSharedValue(0.3);
    const scale = useSharedValue(0.8);

    useEffect(() => {
        opacity.value = withDelay(
            delay,
            withRepeat(
                withTiming(1, { duration: 600, easing: Easing.ease }),
                -1,
                true
            )
        );
        scale.value = withDelay(
            delay,
            withRepeat(
                withTiming(1.2, { duration: 600, easing: Easing.ease }),
                -1,
                true
            )
        );
    }, []);

    const animatedStyle = useAnimatedStyle(() => ({
        opacity: opacity.value,
        transform: [{ scale: scale.value }],
    }));

    return <Animated.View style={[styles.dot, animatedStyle]} />;
};

// Simple elegant spinner alternative
const ElegantSpinner: React.FC = () => {
    const rotation = useSharedValue(0);

    useEffect(() => {
        rotation.value = withRepeat(
            withTiming(360, { duration: 1000, easing: Easing.linear }),
            -1,
            false
        );
    }, []);

    const animatedStyle = useAnimatedStyle(() => ({
        transform: [{ rotate: `${rotation.value}deg` }],
    }));

    return (
        <View style={styles.elegantSpinnerContainer}>
            <View style={styles.elegantSpinnerTrack} />
            <Animated.View style={[styles.elegantSpinnerActive, animatedStyle]}>
                <View style={styles.elegantSpinnerArc} />
            </Animated.View>
        </View>
    );
};

export const DetailScreenLoading: React.FC<DetailScreenLoadingProps> = ({
    message = 'Loading...',
    showMessage = true,
}) => {
    return (
        <View style={styles.container}>
            <View style={styles.content}>
                <ElegantSpinner />
                {/* <Spinner /> */}
                
                {showMessage && (
                    <View style={styles.messageContainer}>
                        <Text style={styles.message}>{message}</Text>
                        <View style={styles.dotsContainer}>
                            <AnimatedDot delay={0} />
                            <AnimatedDot delay={200} />
                            <AnimatedDot delay={400} />
                        </View>
                    </View>
                )}
            </View>
        </View>
    );
};

// Compact version for inline loading
export const InlineLoading: React.FC<{ size?: 'small' | 'medium' | 'large' }> = ({
    size = 'medium',
}) => {
    const rotation = useSharedValue(0);

    useEffect(() => {
        rotation.value = withRepeat(
            withTiming(360, { duration: 800, easing: Easing.linear }),
            -1,
            false
        );
    }, []);

    const animatedStyle = useAnimatedStyle(() => ({
        transform: [{ rotate: `${rotation.value}deg` }],
    }));

    const dimensions = {
        small: { size: 20, stroke: 2 },
        medium: { size: 32, stroke: 3 },
        large: { size: 48, stroke: 4 },
    };

    const { size: spinnerSize, stroke } = dimensions[size];

    return (
        <Animated.View
            style={[
                {
                    width: spinnerSize,
                    height: spinnerSize,
                    borderRadius: spinnerSize / 2,
                    borderWidth: stroke,
                    borderColor: theme.colors.border,
                    borderTopColor: theme.colors.primary,
                },
                animatedStyle,
            ]}
        />
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.background,
    },
    content: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    spinnerContainer: {
        width: 80,
        height: 80,
        justifyContent: 'center',
        alignItems: 'center',
    },
    pulsingCircle: {
        position: 'absolute',
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: theme.colors.primary,
    },
    centerDot: {
        width: 16,
        height: 16,
        borderRadius: 8,
        backgroundColor: theme.colors.primary,
    },
    messageContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: theme.spacing.lg,
    },
    message: {
        ...theme.typography.body1,
        color: theme.colors.textSecondary,
        marginRight: theme.spacing.xs,
    },
    dotsContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    dot: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: theme.colors.primary,
    },
    elegantSpinnerContainer: {
        width: 56,
        height: 56,
        justifyContent: 'center',
        alignItems: 'center',
    },
    elegantSpinnerTrack: {
        position: 'absolute',
        width: 56,
        height: 56,
        borderRadius: 28,
        borderWidth: 4,
        borderColor: theme.colors.border,
    },
    elegantSpinnerActive: {
        width: 56,
        height: 56,
        justifyContent: 'center',
        alignItems: 'center',
    },
    elegantSpinnerArc: {
        width: 56,
        height: 56,
        borderRadius: 28,
        borderWidth: 4,
        borderColor: 'transparent',
        borderTopColor: theme.colors.primary,
        borderRightColor: theme.colors.primaryLight,
    },
});

export default DetailScreenLoading;
